import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './scss/TeamPage.scss';
import Header from './Header';
import api from './services/apiService';
import { format } from 'date-fns';
import { FaTrophy, FaCalendarAlt, FaRunning, FaMap, FaUsers, FaInfoCircle, FaExternalLinkAlt, FaPencilAlt } from 'react-icons/fa';
import { authService } from './services/authService';

const TeamPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [teamDetails, setTeamDetails] = useState(null);
  const [players, setPlayers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAcademy, setIsAcademy] = useState(false);
  
  // Check if user is a coach or manager
  const userType = sessionStorage.getItem('user_type');
  const isCoach = userType === 'coach' || userType === 'manager';

  // Function to format phone numbers for UK display
  const formatPhoneNumber = (phone) => {
    if (!phone) return phone;
    
    // Remove any existing formatting
    let cleanPhone = phone.replace(/\s+/g, '').replace(/[()-]/g, '');
    
    // If it starts with +44, convert to UK format
    if (cleanPhone.startsWith('+44')) {
      cleanPhone = '0' + cleanPhone.substring(3);
    }
    
    // If it's a UK mobile number (11 digits starting with 07) or landline (11 digits starting with 01/02/03), format it
    if (cleanPhone.length === 11 && (cleanPhone.startsWith('07') || cleanPhone.startsWith('01') || cleanPhone.startsWith('02') || cleanPhone.startsWith('03'))) {
      return cleanPhone.replace(/(\d{5})(\d{6})/, '$1 $2');
    }
    
    // If it's a 10-digit number starting with 7 (mobile without 0), add the 0
    if (cleanPhone.length === 10 && cleanPhone.startsWith('7')) {
      return '0' + cleanPhone.replace(/(\d{5})(\d{5})/, '$1 $2');
    }
    
    // If it's a 10-digit number starting with 1,2,3 (landline without 0), add the 0
    if (cleanPhone.length === 10 && (cleanPhone.startsWith('1') || cleanPhone.startsWith('2') || cleanPhone.startsWith('3'))) {
      return '0' + cleanPhone.replace(/(\d{4})(\d{6})/, '$1 $2');
    }
    
    // Return original if no formatting rules match
    return phone;
  };

  // Dummy data for development or fallback
  const dummyTeam = {
    team_id: teamId,
    team_name: 'Team Name',
    type: 'academy',
    league_name: 'League Name',
    league_table_url: '',
    location: 'Home Ground',
    county: 'County',
    pitch: 'Pitch Details',
    play_schedule: 'Play Schedule',
    open_to_new_players: 1,
    coaches: [
      { name: 'Coach Name', role: 'Coach', contact: 'Contact details' }
    ],
    admin: { name: 'Admin Name', contact: 'Contact details' },
    emergency_contact: '',
    complaints_procedure: '',
    safeguarding: '',
    safeguarding_url: '',
    age_group: 'Age Group'
  };

  const dummyPlayers = [
    { player_id: 1, first_name: 'Player', last_name: 'One', last_played: '2023-06-15', position: 'Forward' },
    { player_id: 2, first_name: 'Player', last_name: 'Two', last_played: '2023-06-10', position: 'Midfielder' }
  ];

  const dummyManagers = [
    { manager_id: 1, first_name: 'Manager', last_name: 'One', email: 'manager1@example.com', phone: 'Phone number' },
    { manager_id: 2, first_name: 'Manager', last_name: 'Two', email: 'manager2@example.com', phone: 'Phone number' }
  ];

  // Function to load dummy data
  const loadDummyData = () => {
    setTeamDetails(dummyTeam);
    setPlayers(dummyPlayers);
    setManagers(dummyManagers);
    setIsAcademy(dummyTeam.type === 'academy');
    setIsLoading(false);
  };

  // Fetch team details and players
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch team details
        const teamResponse = await api.get(`fetch_team_details.php?team_id=${teamId}`);
        if (teamResponse.data.success) {
          setTeamDetails(teamResponse.data.team);
          setIsAcademy(teamResponse.data.team.type === 'academy');
        } else {
          setError('Failed to fetch team details');
          return false;
        }

        // Fetch team players
        const playersResponse = await api.get(`get_players.php?team_id=${teamId}`);
        if (playersResponse.data.success) {
          setPlayers(playersResponse.data.players);
        } else {
          setError('Failed to fetch players');
          return false;
        }
        
        // Fetch team managers
        try {
          const managersResponse = await api.get(`get_managers.php?team_id=${teamId}`);
          if (managersResponse.data.success) {
            setManagers(managersResponse.data.managers);
          }
        } catch (err) {
          console.warn('Could not load managers, using dummy data', err);
          setManagers(dummyManagers);
        }
        
        return true;
      } catch (err) {
        setError('An error occurred while fetching data');
        console.error(err);
        return false;
      } finally {
        setIsLoading(false);
      }
    };

    // Try to fetch real data, fall back to dummy data if it fails
    fetchData().then(success => {
      if (!success) {
        loadDummyData();
      }
    });
  }, [teamId]);

  // Navigation handlers
  const handleLeagueTableClick = () => {
    if (teamDetails && teamDetails.league_table_url) {
      window.open(teamDetails.league_table_url, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/leaderboard');
    }
  };

  const handleFixturesClick = () => {
    navigate('/matches');
  };

  const handleEditTeam = () => {
    navigate(`/edit-team/${teamId}`);
  };

  if (isLoading) {
    return (
      <div className="team-page">
        <Header />
        <div className="container">
          <div className="loading">Loading team information...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-page">
        <Header />
        <div className="container">
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="team-page">
      <Header />
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Team Information</h1>
          {isCoach && (
            <button className="edit-button" onClick={handleEditTeam}>
              <FaPencilAlt /> Edit Team
            </button>
          )}
        </div>
        
        {/* Info reminder for missing URLs */}
        {isCoach && (!teamDetails.league_table_url || !teamDetails.fixtures_url) && (
          <div className="info-reminder">
            {!teamDetails.league_table_url && (
              <p>Please add the FA League Table URL to enable direct access to your league table.</p>
            )}
            {!teamDetails.fixtures_url && (
              <p>Please add the Fixtures URL to enable direct access to your team fixtures.</p>
            )}
            <button className="edit-reminder-button" onClick={handleEditTeam}>
              Update URLs
            </button>
          </div>
        )}
        
        {/* Actions Section - Moved to top of page */}
        <section className="actions-section">
          <div className="actions-grid">
            {teamDetails.league_table_url && (
              <button className="action-button" onClick={handleLeagueTableClick}>
                <FaTrophy className="button-icon" />
                FA League Table
              </button>
            )}
            
            {teamDetails.fixtures_url && (
              <button 
                className="action-button" 
                onClick={() => window.open(teamDetails.fixtures_url, '_blank', 'noopener,noreferrer')}
              >
                <FaCalendarAlt className="button-icon" />
                Fixtures
              </button>
            )}
          </div>
        </section>
        
        {/* Current Team Details Section */}
        <section className="team-details-section">
          <h2 className="section-title">
            <FaInfoCircle className="section-icon" />
            Current Team Details
          </h2>
          <div className="team-details-grid">
            <div className="detail-item">
              <div className="detail-label">Team Name</div>
              <div className="detail-value">{teamDetails.team_name}</div>
            </div>
            
            <div className="detail-item">
              <div className="detail-label">League Name</div>
              <div className="detail-value">{teamDetails.league_name || 'Not in a league'}</div>
            </div>
            
            <div className="detail-item">
              <div className="detail-label">Home Ground</div>
              <div className="detail-value">{teamDetails.location}{teamDetails.pitch ? ` - ${teamDetails.pitch}` : ''}</div>
            </div>
            
            <div className="detail-item">
              <div className="detail-label">Play Schedule</div>
              <div className="detail-value">{teamDetails.play_schedule || 'Schedule not set'}</div>
            </div>
            
            {isAcademy && teamDetails.age_group && (
              <div className="detail-item">
                <div className="detail-label">Age Group</div>
                <div className="detail-value">{teamDetails.age_group}</div>
              </div>
            )}
          </div>
        </section>
        
        {/* Player List Section */}
        <section className="players-section">
          <h2 className="section-title">
            <FaUsers className="section-icon" />
            Player List
          </h2>
          <div className="players-grid">
            {players.length > 0 ? (
              players.map(player => (
                <div key={player.player_id} className="player-card">
                  <div className="player-name">{player.first_name} {player.last_name}</div>
                  {player.last_played && (
                    <div className="player-info">
                      Last played: {format(new Date(player.last_played), 'dd MMM yyyy')}
                    </div>
                  )}
                  {player.position && (
                    <div className="player-position">{player.position}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-players-message">No players found for this team</div>
            )}
          </div>
        </section>
        
        {/* Team Management Section - Shown for all team types */}
        <section className="team-management-section">
          <h2 className="section-title">
            <FaMap className="section-icon" />
            Team Management
          </h2>
            <div className="management-grid">
              {/* Team Managers/Coaches */}
              {managers && managers.length > 0 ? (
                managers.map((manager, index) => (
                  <div key={index} className="management-item">
                    <div className="management-label">Coach</div>
                    <div className="management-value">{manager.first_name} {manager.last_name}</div>
                    {manager.email && <div className="management-contact">{manager.email}</div>}
                    {manager.phone && <div className="management-contact">{formatPhoneNumber(manager.phone)}</div>}
                  </div>
                ))
              ) : (
                <div className="management-item">
                  <div className="management-label">Coach</div>
                  <div className="management-value">Contact club for details</div>
                </div>
              )}
              
              {/* Team Contact */}
              {teamDetails.admin && (
                <div className="management-item">
                  <div className="management-label">Team Contact</div>
                  <div className="management-value">{teamDetails.admin.name}</div>
                  {teamDetails.admin.contact && <div className="management-contact">{teamDetails.admin.contact}</div>}
                  <div className="management-description">This is the person responsible for team inquiries, payments, and general information</div>
                </div>
              )}
              
              {/* Emergency Contact */}
              <div className="management-item full-width">
                <div className="management-label">Emergency Contact Details</div>
                {teamDetails.emergency_contact ? (
                  <div>
                    {teamDetails.emergency_contact.split('\n').map((line, i) => (
                      line ? (
                        i === 0 ? (
                          <div key={i} className="management-value">{line}</div>
                        ) : (
                          <div key={i} className="management-contact">{line}</div>
                        )
                      ) : null
                    ))}
                  </div>
                ) : (
                  <div className="management-value">Contact club for emergency details</div>
                )}
              </div>
              
              {/* Complaints Procedure */}
              <div className="management-item full-width">
                <div className="management-label">Complaints Procedure</div>
                <div className="management-value">
                  {teamDetails.complaints_procedure || 'Please contact the team administrator for complaints procedure details'}
                </div>
              </div>
              
              {/* Safeguarding - only shown for academy teams */}
              {isAcademy && (
                <div className="management-item full-width">
                  <div className="management-label">Safeguarding</div>
                  <div className="management-value">
                    {teamDetails.safeguarding || 'Contact your team administrator for safeguarding concerns'}
                  </div>
                  {teamDetails.safeguarding_url && (
                    <div className="management-contact">
                      <a 
                        href={teamDetails.safeguarding_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="safeguarding-link"
                      >
                        Visit our Safeguarding Page <FaExternalLinkAlt size={12} />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
      </div>
    </div>
  );
};

export default TeamPage; 